import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, refetchOnWindowFocus: false },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
// Build stamp — a real runtime side effect (survives minification) so each release
// compiles to a NEW main.[hash].js filename, busting Cloudflare + service-worker URL caches.
const APP_BUILD = "2026-06-05-spark-voice-9";
document.documentElement.setAttribute("data-build", APP_BUILD);
root.render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);

// --- PWA: capture the install prompt + register the auto-updating service worker ---
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__deferredInstallPrompt = e;
  window.dispatchEvent(new CustomEvent("codex:pwa-installable"));
});
window.addEventListener("appinstalled", () => {
  window.__deferredInstallPrompt = null;
  window.dispatchEvent(new CustomEvent("codex:pwa-installed"));
});

if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    // Whether this page was ALREADY controlled by a SW at load time. If not, the first
    // controllerchange is just the SW taking initial control (via clients.claim) — we must
    // NOT reload for that (it causes a "load then reload" flash on first visit / Incognito).
    const hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.register("/service-worker.js", { updateViaCache: "none" }).then((reg) => {
      reg.update().catch(() => {});  // always check for a newer SW on load
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          // A new SW is installed AND an old one is controlling the page => update ready.
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            window.dispatchEvent(new CustomEvent("codex:sw-update", { detail: reg }));
          }
        });
      });
    }).catch(() => { /* SW optional; ignore */ });

    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded || !hadController) return;  // only reload on a genuine UPDATE, never first control
      reloaded = true;
      window.location.reload();
    });
  });
} else if ("serviceWorker" in navigator) {
  // DEV / preview only: the dev server serves non-fingerprinted bundles, so a cache-first
  // SW would serve a stale bundle and fight HMR -> endless reloads. Make sure no previously
  // registered SW (or its caches) hijacks the preview.
  navigator.serviceWorker.getRegistrations?.()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
  if (window.caches) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
  }
}
